import axios from "axios";
import { getApiBase } from "./apiBase";

const BASE = getApiBase();

// Dashboard and record endpoints are the data contract required by these screens.
export const getDashboardSummary = () =>
  axios.get(`${BASE}/dashboard/summary`, { headers: authHeader() }).then((r) => r.data);

export const getDashboardStats = () =>
  axios.get(`${BASE}/dashboard/stats`, { headers: authHeader() }).then((r) => r.data);

export const getManagedRecords = (params = {}) =>
  axios.get(`${BASE}/records`, { headers: authHeader(), params }).then((r) => r.data);

export const getRecordActivities = () =>
  axios.get(`${BASE}/records/activities`, { headers: authHeader() }).then((r) => r.data);

export const submitEvidenceRecord = ({ activityId, evidenceUrl, evidenceFile }) => {
  const body = new FormData();
  body.append("activityId", activityId);
  if (evidenceUrl) body.append("evidenceUrl", evidenceUrl);
  if (evidenceFile) body.append("evidenceFile", evidenceFile);
  return axios.post(`${BASE}/records`, body, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const submitMentorEvidenceRecord = ({ menteeId, activityTitle, evidenceUrl, evidenceFile }) => {
  const body = new FormData();
  body.append("menteeId", menteeId);
  body.append("activityTitle", activityTitle);
  if (evidenceUrl) body.append("evidenceUrl", evidenceUrl);
  if (evidenceFile) body.append("evidenceFile", evidenceFile);
  return axios.post(`${BASE}/records/mentor-evidence`, body, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const verifyEvidenceLink = (url, evidenceId) =>
  axios.post(`${BASE}/records/verify-link`, { url, ...(evidenceId ? { evidenceId } : {}) }, { headers: authHeader(), timeout: 15000 }).then((r) => r.data);

export const reviewEvidenceRecord = (recordId, status, reviewNote) =>
  axios.patch(`${BASE}/records/${encodeURIComponent(recordId)}/review`, { status, reviewNote }, { headers: authHeader() }).then((r) => r.data);

export const getMentors = () =>
  axios.get(`${BASE}/mentors`, { headers: authHeader() }).then((r) => r.data);

export const getMentorMentees = (mentorId) =>
  axios.get(`${BASE}/mentors/${encodeURIComponent(mentorId)}/mentees`, { headers: authHeader() }).then((r) => r.data);

export const assignMenteeToMentor = (mentorId, data) =>
  axios.post(`${BASE}/mentors/${encodeURIComponent(mentorId)}/mentees`, data, { headers: authHeader() }).then((r) => r.data);

export const createMenteeProfile = (mentorId, data) =>
  axios.post(`${BASE}/mentors/${encodeURIComponent(mentorId)}/mentees/profile`, data, { headers: authHeader() }).then((r) => r.data);

export const updateEvidenceRecord = ({ recordId, activityTitle, evidenceUrl, evidenceFile }) => {
  const body = new FormData();
  if (activityTitle !== undefined) body.append("activityTitle", activityTitle);
  if (evidenceUrl) body.append("evidenceUrl", evidenceUrl);
  if (evidenceFile) body.append("evidenceFile", evidenceFile);
  return axios.put(`${BASE}/records/${encodeURIComponent(recordId)}/evidence`, body, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const deleteEvidenceRecord = (recordId) =>
  axios.delete(`${BASE}/records/${encodeURIComponent(recordId)}`, { headers: authHeader() }).then((r) => r.data);

/** Strip accidental quotes / Bearer prefix / whitespace from stored JWT. */
export function getAuthToken() {
  let token = localStorage.getItem("token");
  if (!token || token === "undefined" || token === "null") return null;
  token = String(token).trim();
  // If token was JSON.stringified into localStorage → "\"eyJ...\""
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    try {
      token = JSON.parse(token);
    } catch {
      token = token.slice(1, -1);
    }
  }
  token = String(token || "").trim();
  if (/^Bearer\s+/i.test(token)) token = token.replace(/^Bearer\s+/i, "").trim();
  if (!token || token.split(".").length !== 3) return null;
  return token;
}

/** True when a JWT exists and is not past exp (client-side check). */
export function isAuthTokenValid() {
  const token = getAuthToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload?.exp && payload.exp * 1000 <= Date.now() - 5001) return false;
    return true;
  } catch {
    return false;
  }
}

function clearAuthSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("ap_user");
  try {
    sessionStorage.setItem("ap_auth_notice", "session_expired");
  } catch (_) {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent("ap:auth-expired"));
  } catch (_) {
    /* ignore */
  }
}

// ── Auth header helper ─────────────────────────────────────────────────────────
function authHeader() {
  const token = getAuthToken();
  if (!token) return {};
  // Heal localStorage if it was previously corrupted
  if (localStorage.getItem("token") !== token) {
    localStorage.setItem("token", token);
  }
  return { Authorization: `Bearer ${token}` };
}

// Attach Authorization on every API call; force re-login on expired/invalid JWT
axios.interceptors.request.use((config) => {
  if (!config.url || !String(config.url).includes(BASE)) return config;
  const headers = authHeader();
  if (headers.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = headers.Authorization;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const msg = String(err?.response?.data?.error || "");
    if (
      status === 401 &&
      (/token|jwt|session expired|please log in|unauthorized/i.test(msg) ||
        msg === "No token provided")
    ) {
      clearAuthSession();
    }
    return Promise.reject(err);
  }
);

// ── Existing endpoints (unchanged) ────────────────────────────────────────────
export const checkHealth = () => axios.get(`${BASE}/health`).then(r => r.data);

export const generatePlan = (module, inputs, feedback = "", dailyRows = null) =>
  axios.post(`${BASE}/generate`, { module, inputs, feedback, dailyRows: dailyRows || undefined }).then(r => r.data);

export const refinePlan = (module, existingPlan, feedback, opts = {}) =>
  axios
    .post(`${BASE}/refine`, {
      module,
      existingPlan,
      feedback,
      ...(opts.focusDayKey ? { focusDayKey: opts.focusDayKey } : {}),
    })
    .then((r) => r.data);

export const polishPlan = (module, existingPlan) =>
  axios.post(`${BASE}/polish`, { module, existingPlan }).then(r => r.data);

export const generateAllPlans = (inputs) => {
  const mustApply = String(inputs?._mustApplyText || inputs?._forceInstructions || "").trim();
  return axios
    .post(
      `${BASE}/generate-all`,
      {
        inputs,
        ...(mustApply ? { feedback: mustApply, forceApply: true } : {}),
      },
      { timeout: 600000 }
    )
    .then((r) => r.data);
};

export const lookupScheduleRepo = (inputs) =>
  axios
    .post(`${BASE}/schedule-repo/lookup`, { inputs }, { headers: authHeader() })
    .then((r) => r.data);

export const saveScheduleRepo = (payload) =>
  axios
    .post(`${BASE}/schedule-repo/save`, payload, { headers: authHeader() })
    .then((r) => r.data);

export const listScheduleRepo = (params = {}) =>
  axios
    .get(`${BASE}/schedule-repo`, {
      headers: authHeader(),
      params:
        typeof params === "number"
          ? { limit: params }
          : {
              limit: params.limit || 20,
              ...(params.softFingerprint ? { softFingerprint: params.softFingerprint } : {}),
              ...(params.cohortKey ? { cohortKey: params.cohortKey } : {}),
              ...(params.campus ? { campus: params.campus } : {}),
              ...(params.department ? { department: params.department } : {}),
              ...(params.year ? { year: params.year } : {}),
              ...(params.q ? { q: params.q } : {}),
            },
    })
    .then((r) => r.data);

export const getScheduleRepoEntry = (id) =>
  axios
    .get(`${BASE}/schedule-repo/${id}`, { headers: authHeader() })
    .then((r) => r.data);

export const uploadFiles = (files) => {
  const fd = new FormData();
  Object.entries(files).forEach(([key, arr]) => arr.forEach(f => fd.append(key, f)));
  return axios.post(`${BASE}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

export const listUploads = () =>
  axios.get(`${BASE}/uploads`, { headers: authHeader() }).then((r) => r.data);

export const signup = (data) => axios.post(`${BASE}/signup`, data);
export const login  = (data) => axios.post(`${BASE}/login`, data);
export const getSignupInstitutions = () =>
  axios.get(`${BASE}/signup/institutions`).then((r) => r.data);
export const getSignupDepartments = (institutionId) =>
  axios.get(`${BASE}/signup/departments`, { params: { institutionId } }).then((r) => r.data);
export const forgotPassword = (data) => axios.post(`${BASE}/forgot-password`, data).then((r) => r.data);
export const resetPassword = (data) => axios.post(`${BASE}/reset-password`, data).then((r) => r.data);

export const getCalendar = (startDate, endDate, exams = []) =>
  axios.post(`${BASE}/calendar`, { startDate, endDate, exams }).then(r => r.data);

// ── Leave Management ──────────────────────────────────────────────────────────
export const setBasePlan = (plan) =>
  axios.post(`${BASE}/leave/set-base`, { plan }, { headers: authHeader() }).then(r => r.data);

export const getLeaves = () =>
  axios.get(`${BASE}/leave`, { headers: authHeader() }).then(r => r.data);

export const addLeave = (startDate, endDate, reason) =>
  axios.post(`${BASE}/leave/add`, { startDate, endDate, reason }, { headers: authHeader() }).then(r => r.data);

export const removeLeave = (leaveId) =>
  axios.post(`${BASE}/leave/remove`, { leaveId }, { headers: authHeader() }).then(r => r.data);

// ── Subjects (shared across IA Scheduler, Lecture Notes, Faculty Assistant) ──
export const getSubjects = () =>
  axios.get(`${BASE}/subjects`, { headers: authHeader() }).then(r => r.data);

export const addSubject = (name, syllabusText) =>
  axios.post(`${BASE}/subjects`, { name, syllabusText }, { headers: authHeader() }).then(r => r.data);

export const deleteSubject = (id) =>
  axios.delete(`${BASE}/subjects/${id}`, { headers: authHeader() }).then(r => r.data);

// ── Version History ───────────────────────────────────────────────────────────
export const getVersions = (params = {}) =>
  axios.get(`${BASE}/versions`, {
    headers: authHeader(),
    params: {
      memberKey: params.memberKey || undefined,
    },
  }).then(r => r.data);

export const getVersion = (versionNum) =>
  axios.get(`${BASE}/versions/${versionNum}`, { headers: authHeader() }).then(r => r.data);

export const compareVersions = (vA, vB) =>
  axios
    .post(
      `${BASE}/versions/compare`,
      { vA: Number(vA), vB: Number(vB) },
      { headers: authHeader() }
    )
    .then((r) => r.data)
    .catch(async (err) => {
      // Fallback to GET if POST route not yet deployed
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        const r = await axios.get(`${BASE}/versions/compare/${vA}/${vB}`, {
          headers: authHeader(),
        });
        return r.data;
      }
      throw err;
    });

export const restoreVersion = (versionNum) =>
  axios.post(`${BASE}/versions/restore/${versionNum}`, {}, { headers: authHeader() }).then(r => r.data);

export const saveVersion = (plan, changeType, reason, member = {}, extras = {}) => {
  const memberName =
    member.memberName || member.name || member.personName || undefined;
  const memberEmail =
    member.memberEmail || member.email || undefined;
  const memberKey =
    member.memberKey ||
    (memberEmail
      ? String(memberEmail).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
      : memberName
        ? String(memberName).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
        : undefined);
  let actorEmail =
    extras.changedByEmail ||
    member.changedByEmail ||
    undefined;
  if (!actorEmail) {
    try {
      const raw = localStorage.getItem("user");
      const u = raw ? JSON.parse(raw) : null;
      actorEmail = u?.email || undefined;
    } catch (_) {}
  }
  return axios.post(`${BASE}/versions/save`, {
    plan,
    changeType,
    reason,
    memberKey: memberKey || undefined,
    memberName: memberName || undefined,
    memberEmail: memberEmail || undefined,
    email: memberEmail || undefined,
    name: memberName || undefined,
    trigger: extras.trigger || member.trigger || undefined,
    changedBy: extras.changedBy || member.changedBy || undefined,
    changedByEmail: actorEmail || undefined,
    protected: extras.protected ?? member.protected ?? undefined,
    manualOverride: extras.manualOverride ?? member.manualOverride ?? undefined,
    protectedSlots: extras.protectedSlots || member.protectedSlots || undefined,
  }, { headers: authHeader() }).then(r => r.data);
};

export const setVersionProtected = (versionNum, protectedFlag = true, protectedSlots) =>
  axios.post(`${BASE}/versions/${versionNum}/protect`, {
    protected: protectedFlag,
    protectedSlots,
  }, { headers: authHeader() }).then(r => r.data);

export const deleteVersion = (versionNum) =>
  axios.delete(`${BASE}/versions/${versionNum}`, { headers: authHeader() }).then(r => r.data);

export const getAuditLogs = (params = {}) =>
  axios
    .get(`${BASE}/audit-logs`, {
      headers: authHeader(),
      params: {
        entity: params.entity || undefined,
        action: params.action || undefined,
        memberKey: params.memberKey || undefined,
        limit: params.limit || 50,
        skip: params.skip || 0,
        from: params.from || undefined,
        to: params.to || undefined,
      },
    })
    .then((r) => r.data);

// ── Blueprint Management ──────────────────────────────────────────────────────
export const getBlueprints = () =>
  axios.get(`${BASE}/blueprint`, { headers: authHeader() }).then(r => r.data);

export const getActiveBlueprint = () =>
  axios.get(`${BASE}/blueprint/active`, { headers: authHeader() }).then(r => r.data);

export const createBlueprint = (data) =>
  axios.post(`${BASE}/blueprint`, data, { headers: authHeader() }).then(r => r.data);

export const activateBlueprint = (versionNum) =>
  axios.post(`${BASE}/blueprint/activate/${versionNum}`, {}, { headers: authHeader() }).then(r => r.data);

export const deactivateBlueprint = (versionNum) =>
  axios.post(`${BASE}/blueprint/deactivate/${versionNum}`, {}, { headers: authHeader() }).then(r => r.data);

export const deleteBlueprint = (versionNum) =>
  axios.delete(`${BASE}/blueprint/${versionNum}`, { headers: authHeader() }).then(r => r.data);

// ── Difficulty-Aware Scheduler ────────────────────────────────────────────────
export const detectDifficulty = (subjects) =>
  axios.post(`${BASE}/scheduler/detect-difficulty`, { subjects }, { headers: authHeader() }).then(r => r.data);

export const buildIASchedule = (subjects, examDays, constraints) =>
  axios.post(`${BASE}/scheduler/ia`, { subjects, examDays, constraints }, { headers: authHeader() }).then(r => r.data);

// ── Daily Plan Locking & Regeneration ────────────────────────────────────────
export const syncDailyPlan = (plan) =>
  axios.post(`${BASE}/daily/sync`, { plan }, { headers: authHeader() }).then(r => r.data);

export const getDailyPlan = () =>
  axios.get(`${BASE}/daily/plan`, { headers: authHeader() }).then(r => r.data);

export const getBasePlan = () =>
  axios.get(`${BASE}/leave/base`, { headers: authHeader() }).then(r => r.data);

export const getWeeklyPlan = () =>
  axios.get(`${BASE}/weekly`, { headers: authHeader() }).then(r => r.data);

export const syncWeeklyPlan = (plan) =>
  axios.post(`${BASE}/weekly/sync`, { plan }, { headers: authHeader() }).then(r => r.data);

// ── Tonight's Homework (separate collection) ──────────────────────────────────
export const getHomeworkPlan = () =>
  axios.get(`${BASE}/homework`, { headers: authHeader() }).then(r => r.data);

export const syncHomeworkPlan = (plan, opts = {}) =>
  axios.post(
    `${BASE}/homework/sync`,
    { plan, source: opts.source || "client-save", replace: opts.replace !== false },
    { headers: authHeader() }
  ).then(r => r.data);

export const upsertHomeworkDay = (payload) =>
  axios.put(`${BASE}/homework/day`, payload, { headers: authHeader() }).then(r => r.data);

export const toggleHomeworkTask = (dayId, taskIndex, done) =>
  axios.post(
    `${BASE}/homework/toggle-task`,
    { dayId, taskIndex, done },
    { headers: authHeader() }
  ).then(r => r.data);

export const toggleHomeworkNight = (dayId, done) =>
  axios.post(
    `${BASE}/homework/toggle-night`,
    { dayId, done },
    { headers: authHeader() }
  ).then(r => r.data);

export const resetHomeworkProgress = (dayId = null) =>
  axios.post(
    `${BASE}/homework/reset-progress`,
    dayId ? { dayId } : {},
    { headers: authHeader() }
  ).then(r => r.data);

export const getPlannerInputs = () =>
  axios.get(`${BASE}/planner-inputs`, { headers: authHeader() }).then(r => r.data);

export const savePlannerInputs = (payload) =>
  axios.post(`${BASE}/planner-inputs`, payload, { headers: authHeader() }).then(r => r.data);

export const getDailyStatus = () =>
  axios.get(`${BASE}/daily/status`, { headers: authHeader() }).then(r => r.data);

export const lockDay = (dayId) =>
  axios.post(`${BASE}/daily/lock`, { dayId }, { headers: authHeader() }).then(r => r.data);

export const unlockDay = (dayId) =>
  axios.post(`${BASE}/daily/unlock`, { dayId }, { headers: authHeader() }).then(r => r.data);

export const regenerateDay = (dayId, context, rows, member = {}) =>
  axios
    .post(
      `${BASE}/daily/regenerate`,
      {
        dayId,
        context,
        rows,
        memberName: member.memberName || member.name || undefined,
        memberKey: member.memberKey || undefined,
        memberEmail: member.memberEmail || member.email || undefined,
        name: member.memberName || member.name || undefined,
        email: member.memberEmail || member.email || undefined,
      },
      { headers: authHeader() }
    )
    .then((r) => r.data);

export const toggleRowDone = (dayId, rowIndex, done) =>
  axios.post(`${BASE}/daily/toggle-row`, { dayId, rowIndex, done }, { headers: authHeader() }).then(r => r.data);

export const getDoneRows = (dayId) =>
  axios.get(`${BASE}/daily/done-rows/${encodeURIComponent(dayId)}`, { headers: authHeader() }).then(r => r.data);

// ── Disruption Management ──────────────────────────────────────────────────────
export const applyDisruption = (date, blockedSlots, alternativeType, reason, customTitle = "") =>
  axios.post(`${BASE}/disruption/apply`, { 
    date, 
    blockedSlots, 
    alternativeType, 
    reason,
    customTitle 
  }, { headers: authHeader() }).then(r => r.data);

  export const applyDayDisruption = (date, alternativeType, reason, customTitle = "") =>
  axios.post(`${BASE}/leave/disruption`, { 
    date, alternativeType, reason, customTitle,
  }, { headers: authHeader() }).then(r => r.data);


  export const applyDateRangeDisruption = (startDate, endDate, alternativeType, reason, customTitle = "", blockedSlots = []) =>
  axios.post(`${BASE}/leave/disruption`, {
    startDate, endDate, blockedSlots, alternativeType, reason, customTitle,
  }, { headers: authHeader() }).then(r => r.data);


export const applyWeekDisruption = (weekNumber, alternativeType, reason, customTitle = "", blockedSlots = []) =>
  axios.post(`${BASE}/leave/disruption`, {
    weekNumber, blockedSlots, alternativeType, reason, customTitle,
  }, { headers: authHeader() }).then(r => r.data);

// ── Get disruptions for a specific date ──────────────────────────────────────
export const getDisruptionsByDate = (date) =>
  axios.get(`${BASE}/disruption/date/${encodeURIComponent(date)}`, { headers: authHeader() })
    .then(r => r.data);

// ── Get all disruptions (latest 50) ──────────────────────────────────────────
export const getAllDisruptions = () =>
  axios.get(`${BASE}/disruption/all`, { headers: authHeader() }).then(r => r.data);

// ── Remove a specific disruption by ID ──────────────────────────────────────
export const removeDisruption = (disruptionId) =>
  axios.delete(`${BASE}/disruption/${disruptionId}`, { headers: authHeader() }).then(r => r.data);

// ── Undo the last disruption for a date ──────────────────────────────────────
export const undoLastDisruption = (date) =>
  axios.post(`${BASE}/disruption/undo`, { date }, { headers: authHeader() }).then(r => r.data);

// ── Clear all disruptions for a date ─────────────────────────────────────────
export const clearDisruptionsForDate = (date) =>
  axios.delete(`${BASE}/disruption/clear-date/${encodeURIComponent(date)}`, { headers: authHeader() })
    .then(r => r.data);
    

    export const swapAcrossDays = (dayKeyA, dayKeyB, mode, slots = []) =>
  axios.post(`${BASE}/disruption/swap`, { dayKeyA, dayKeyB, mode, slots }, { headers: authHeader() }).then(r => r.data);

export const undoSwapAcrossDays = (swapId) =>
  axios.post(`${BASE}/disruption/undo-swap`, { swapId }, { headers: authHeader() }).then(r => r.data);

export const getSwapsForDay = (dayKey) =>
  axios.get(`${BASE}/disruption/swaps/${encodeURIComponent(dayKey)}`, { headers: authHeader() }).then(r => r.data);

// ── Lecture Notes AI Generator ────────────────────────────────────────────────
export const generateLectureNotes = (subject, topic, syllabusText, blueprint, type = "all") =>
  axios.post(`${BASE}/lecture-notes`, { subject, topic, syllabusText, blueprint, type }, { headers: authHeader() }).then(r => r.data);

// ── Faculty Assistant AI ──────────────────────────────────────────────────────
export const generateFacultyActivity = (subject, topic, duration, learningOutcome, syllabusText) =>
  axios.post(`${BASE}/faculty-assistant`, { subject, topic, duration, learningOutcome, syllabusText }, { headers: authHeader() }).then(r => r.data);

// ── Question Bank ─────────────────────────────────────────────────────────────
export const generateQuestionBank = (subject, topic, syllabusText, counts, difficulty, manualInstructions) =>
  axios.post(`${BASE}/question-bank/generate`, { subject, topic, syllabusText, counts, difficulty, manualInstructions }, { headers: authHeader() }).then(r => r.data);

export const getQuestionBanks = () =>
  axios.get(`${BASE}/question-bank`, { headers: authHeader() }).then(r => r.data);

export const getQuestionBank = (id) =>
  axios.get(`${BASE}/question-bank/${id}`, { headers: authHeader() }).then(r => r.data);

export const deleteQuestionBank = (id) =>
  axios.delete(`${BASE}/question-bank/${id}`, { headers: authHeader() }).then(r => r.data);

// ── Problem Statement ─────────────────────────────────────────────────────────
export const generateProblemStatements = (subjects, clubs, count, difficulty, extra = {}) =>
  axios.post(`${BASE}/problem-statement/generate`, {
    subjects,
    clubs,
    count,
    difficulty,
    uniqueOnly: extra.uniqueOnly !== false,
    excludeAssigned: extra.excludeAssigned !== false,
    excludeFingerprints: extra.excludeFingerprints || [],
    techs: extra.techs || [],
    verticals: extra.verticals || [],
    ...extra,
  }, { headers: authHeader() }).then(r => r.data);

export const getProblemStatements = () =>
  axios.get(`${BASE}/problem-statement`, { headers: authHeader() }).then(r => r.data);

export const deleteProblemStatement = (id) =>
  axios.delete(`${BASE}/problem-statement/${id}`, { headers: authHeader() }).then(r => r.data);

// Lightweight, non-AI creation used to silently back a team schedule with a
// real ProblemStatement id when the user hasn't used the Problem Statement
// generator tool — lets team schedules work straight from the Planner tab.
export const quickCreateProblemStatement = (title, description, extra = {}) =>
  axios.post(`${BASE}/problem-statement/quick-create`, {
    title,
    description,
    uniqueOnly: extra.uniqueOnly !== false,
    claimExclusive: extra.claimExclusive !== false,
    fingerprint: extra.fingerprint || "",
    sourceKind: extra.sourceKind || "",
    company: extra.company || "",
    year: extra.year || "",
    // true = the user chose to plan the same problem someone else already has
    allowDuplicate: extra.allowDuplicate === true,
  }, { headers: authHeader() }).then(r => r.data);

// Does this statement already exist, and who holds it (name / department / year)?
export const checkProblemStatement = (title, description = "") =>
  axios.post(`${BASE}/problem-statement/check`, { title, description }, { headers: authHeader() })
    .then(r => r.data);

export const validateProblemFramework = (problemText, useLLM = true, opts = {}) =>
  axios.post(`${BASE}/problem-statement/validate`, {
    problemText,
    useLLM,
    numDays: opts.numDays || opts.days || 5,
    includeOrigin: opts.includeOrigin !== false,
    year: opts.year || "",
    kind: opts.kind || "all",
    window: opts.window || "recent",
    requireYear: true,
  }, { headers: authHeader() }).then(r => r.data);

export const rewriteProblemFramework = (problemText, framework = "all", opts = {}) =>
  axios.post(`${BASE}/problem-statement/rewrite`, {
    problemText,
    framework,
    tech: opts.tech || (opts.techs || [])[0] || "",
    vertical: opts.vertical || (opts.verticals || [])[0] || "",
    techs: opts.techs || (opts.tech ? [opts.tech] : []),
    verticals: opts.verticals || (opts.vertical ? [opts.vertical] : []),
    mode: opts.mode || "",
    seed: opts.seed || "",
    numDays: opts.numDays || opts.days || 5,
    count: opts.count || 1,
    uniqueOnly: opts.uniqueOnly !== false,
    excludeAssigned: opts.excludeAssigned !== false,
    excludeFingerprints: opts.excludeFingerprints || [],
    variation: opts.variation || 0,
    sortBy: opts.sortBy || "",
    year: opts.year || "",
    kind: opts.kind || "",
    requireYear: opts.requireYear !== false,
    detailed: opts.detailed === true,
    minWords: opts.minWords || 0,
  }, { headers: authHeader() }).then(r => r.data);

export const getYCInspiration = (forceRefresh = false, opts = {}) => {
  const q = new URLSearchParams();
  if (forceRefresh) q.set("refresh", "1");
  if (opts.mode) q.set("mode", opts.mode);
  if (opts.kind) q.set("kind", opts.kind);
  if (opts.year != null && opts.year !== "") q.set("year", String(opts.year));
  if (opts.month != null && opts.month !== "") q.set("month", String(opts.month));
  if (opts.window) q.set("window", opts.window);
  const qs = q.toString();
  return axios
    .get(`${BASE}/problem-statement/yc-inspiration${qs ? `?${qs}` : ""}`, { headers: authHeader() })
    .then((r) => r.data);
};

export const getThrustAreas = () =>
  axios.get(`${BASE}/problem-statement/thrust-areas`, { headers: authHeader() }).then(r => r.data);

export const listDreamCompanies = () =>
  axios.get(`${BASE}/dream-companies`, { headers: authHeader() }).then((r) => r.data);

export const checkDreamCompany = (name) =>
  axios.post(`${BASE}/dream-companies/check`, { name }, { headers: authHeader() }).then((r) => r.data);

export const addDreamCompany = (name, force = false) =>
  axios
    .post(`${BASE}/dream-companies`, { name, force }, { headers: authHeader() })
    .then((r) => r.data)
    .catch((err) => {
      if (err.response?.data) return Promise.reject(err);
      throw err;
    });

export const getProfileSuggestions = (kind = "interest", query = "") => {
  const q = new URLSearchParams();
  q.set("kind", kind);
  if (query) q.set("q", query);
  return axios
    .get(`${BASE}/profile-suggestions?${q.toString()}`, { headers: authHeader() })
    .then((r) => r.data);
};

//checkbox for subtask
export async function toggleSubtaskDone(dayId, rowIndex, subIndex, done) {
  const res = await axios.post(
    `${BASE}/daily/toggle-subtask`,
    { dayId, rowIndex, subIndex, done },
    { headers: authHeader() }
  );
  return res.data;
}

export async function getWeekReadiness(weekNum) {
  const res = await axios.get(`${BASE}/daily/readiness/${weekNum}`, { headers: authHeader() });
  return res.data;
}

// ── Build / rebuild Friday assessment
export async function buildAssessment(body) {
  const res = await axios.post(`${BASE}/daily/assessment`, body, { headers: authHeader() });
  return res.data;
}

// ── Get current assessment for a week
export async function getAssessment(weekNum) {
  const res = await axios.get(`${BASE}/daily/assessment/${weekNum}`, { headers: authHeader() });
  return res.data;
}
// ── Team Members (persist to DB) ──────────────────────────────────────────────
export const saveTeamMembersBulk = (problemStatementId, teamName, members) =>
  axios.post(`${BASE}/team/members/bulk`, { problemStatementId, teamName, members }, { headers: authHeader() }).then(r => r.data);

export const getTeamMembers = (problemStatementId, teamName) =>
  axios.get(`${BASE}/team/members`, { params: { problemStatementId, teamName }, headers: authHeader() }).then(r => r.data);

export const deleteTeamMember = (id) =>
  axios.delete(`${BASE}/team/members/${id}`, { headers: authHeader() }).then(r => r.data);

export const generateTeamSchedule = (problemStatementId, teamName, days, dailyRows, activeDTStage) =>
  axios.post(`${BASE}/team/generate-schedule`, {
    problemStatementId,
    teamName,
    days,
    dailyRows,
    activeDTStage: activeDTStage || undefined,
  }, { headers: authHeader() }).then(r => r.data);

export const getTeamSchedule = (problemStatementId, teamName) =>
  axios.get(`${BASE}/team/schedule`, { params: { problemStatementId, teamName }, headers: authHeader() }).then(r => r.data);

export const saveTeamScheduleEdits = (problemStatementId, teamName, schedule, members) =>
  axios.post(`${BASE}/team/schedule/save`, {
    problemStatementId,
    teamName,
    schedule,
    members,
  }, { headers: authHeader() }).then((r) => r.data);

// ── Assessment Content banks (Path Builder) ───────────────────────────────────
export const getAssessmentBanks = () =>
  axios.get(`${BASE}/assessment/banks`, { headers: authHeader() }).then(r => r.data);

export const previewAssessmentPick = (day = 0, config) =>
  axios.get(`${BASE}/assessment/pick`, {
    params: { day, config: config ? JSON.stringify(config) : undefined },
    headers: authHeader(),
  }).then(r => r.data);

/** R2023 campus syllabus by college / dept / year / sem */
export const fetchCampusSyllabus = ({ college, department, collegeYear, semester }) =>
  axios
    .get(`${BASE}/campus-syllabus`, {
      params: { college, department, collegeYear, semester },
    })
    .then((r) => r.data);

